import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { forgotPassword, confirmForgotPassword } from "../auth/cognito-auth";

type ResetStep = "login" | "enterEmail" | "enterCode" | "success";

interface LoginFormProps {
  onSubmit: (email: string, password: string, rememberDevice: boolean) => void;
  isLoading: boolean;
  error?: string;
}

export const LoginForm: React.FC<LoginFormProps> = ({
  onSubmit,
  isLoading,
  error,
}) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberDevice, setRememberDevice] = useState(true);

  // Forgot password state
  const [resetStep, setResetStep] = useState<ResetStep>("login");
  const [resetEmail, setResetEmail] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) return;
    onSubmit(email, password, rememberDevice);
  };

  const handleForgotClick = () => {
    setResetEmail(email);
    setResetError("");
    setResetStep("enterEmail");
  };

  const handleBackToLogin = () => {
    setResetStep("login");
    setResetCode("");
    setNewPassword("");
    setResetError("");
  };

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetEmail) return;
    setResetLoading(true);
    setResetError("");
    try {
      await forgotPassword(resetEmail);
      setResetStep("enterCode");
    } catch (err: any) {
      setResetError(err.message || "Failed to send code");
    } finally {
      setResetLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetCode || !newPassword) return;
    setResetLoading(true);
    setResetError("");
    try {
      await confirmForgotPassword(resetEmail, resetCode, newPassword);
      setResetStep("success");
    } catch (err: any) {
      setResetError(err.message || "Failed to reset password");
    } finally {
      setResetLoading(false);
    }
  };

  const renderResetFlow = () => {
    if (resetStep === "enterEmail") {
      return (
        <form onSubmit={handleSendCode} className="login-form">
          {resetError && (
            <div className="login-error" role="alert">{resetError}</div>
          )}
          <div className="login-field">
            <label htmlFor="reset-email">{t("login.email")}</label>
            <input
              id="reset-email"
              type="email"
              value={resetEmail}
              onChange={(e) => setResetEmail(e.target.value)}
              placeholder={t("login.emailPlaceholder")}
              autoFocus
              required
              disabled={resetLoading}
            />
          </div>
          <button
            type="submit"
            className="login-submit"
            disabled={resetLoading || !resetEmail}
          >
            {resetLoading ? t("login.sendingCode") : t("login.sendCode")}
          </button>
          <button
            type="button"
            className="login-link"
            onClick={handleBackToLogin}
          >
            {t("login.backToLogin")}
          </button>
        </form>
      );
    }

    if (resetStep === "enterCode") {
      return (
        <form onSubmit={handleResetPassword} className="login-form">
          {resetError && (
            <div className="login-error" role="alert">{resetError}</div>
          )}
          <div className="login-field">
            <label htmlFor="reset-code">{t("login.resetCode")}</label>
            <input
              id="reset-code"
              type="text"
              inputMode="numeric"
              value={resetCode}
              onChange={(e) => setResetCode(e.target.value)}
              placeholder={t("login.resetCodePlaceholder")}
              autoFocus
              required
              disabled={resetLoading}
            />
          </div>
          <div className="login-field">
            <label htmlFor="new-password">{t("login.newPassword")}</label>
            <input
              id="new-password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder={t("login.newPasswordPlaceholder")}
              autoComplete="new-password"
              required
              disabled={resetLoading}
            />
          </div>
          <button
            type="submit"
            className="login-submit"
            disabled={resetLoading || !resetCode || !newPassword}
          >
            {resetLoading ? t("login.resettingPassword") : t("login.resetPassword")}
          </button>
          <button
            type="button"
            className="login-link"
            onClick={handleBackToLogin}
          >
            {t("login.backToLogin")}
          </button>
        </form>
      );
    }

    if (resetStep === "success") {
      return (
        <div className="login-form">
          <div className="login-success" role="status">
            {t("login.resetSuccessSubtitle")}
          </div>
          <button
            type="button"
            className="login-submit"
            onClick={handleBackToLogin}
          >
            {t("login.backToLogin")}
          </button>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <h1>
            {resetStep === "success"
              ? t("login.resetSuccess")
              : t("login.title")}
          </h1>
          <p>
            {resetStep === "login"
              ? t("login.subtitle")
              : resetStep === "success"
                ? ""
                : t("login.forgotPasswordSubtitle")}
          </p>
        </div>

        {resetStep !== "login" ? (
          renderResetFlow()
        ) : (
          <form onSubmit={handleSubmit} className="login-form">
            {error && (
              <div className="login-error" role="alert">
                {error}
              </div>
            )}

            <div className="login-field">
              <label htmlFor="email">{t("login.email")}</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("login.emailPlaceholder")}
                autoComplete="username"
                autoFocus
                required
                disabled={isLoading}
              />
            </div>

            <div className="login-field">
              <label htmlFor="password">{t("login.password")}</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t("login.passwordPlaceholder")}
                autoComplete="current-password"
                required
                disabled={isLoading}
              />
              <button
                type="button"
                className="login-link login-forgot"
                onClick={handleForgotClick}
                disabled={isLoading}
              >
                {t("login.forgotPassword")}
              </button>
            </div>

            <div className="login-remember">
              <label>
                <input
                  type="checkbox"
                  checked={rememberDevice}
                  onChange={(e) => setRememberDevice(e.target.checked)}
                  disabled={isLoading}
                />
                <span>{t("login.rememberDevice")}</span>
              </label>
            </div>

            <button
              type="submit"
              className="login-submit"
              disabled={isLoading || !email || !password}
            >
              {isLoading ? t("login.signingIn") : t("login.signIn")}
            </button>
          </form>
        )}
      </div>

      <style>{`
        .login-container {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          position: relative;
          font-family: var(--font-sans);
          /* Paper background to match the landing — soft warm wash on the
             studio surface, no photo. */
          background:
            radial-gradient(120% 100% at 50% 0%, var(--color-surface) 0%, var(--color-bg) 55%, var(--color-accent-ghost) 140%);
        }
        /* faint film grain, same recipe as the landing's editorial system */
        .login-container::before {
          content: "";
          position: absolute;
          inset: 0;
          pointer-events: none;
          opacity: 0.30;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3CfeComponentTransfer%3E%3CfeFuncA type='linear' slope='0.05'/%3E%3C/feComponentTransfer%3E%3C/filter%3E%3Crect width='160' height='160' filter='url(%23n)'/%3E%3C/svg%3E");
        }
        .login-card {
          position: relative;
          z-index: 1;
          background: var(--color-surface);
          border: 1px solid var(--color-border);
          border-radius: 14px;
          box-shadow: 0 8px 32px rgba(28, 36, 32, 0.10);
          padding: 44px 40px;
          width: 100%;
          max-width: 400px;
        }
        .login-header {
          text-align: center;
          margin-bottom: 32px;
        }
        .login-header h1 {
          font-family: var(--font-display);
          font-size: 32px;
          font-weight: 600;
          letter-spacing: -0.01em;
          color: var(--color-title);
          margin: 0 0 8px 0;
        }
        .login-header p {
          color: var(--color-muted);
          margin: 0;
          font-size: 14px;
        }
        .login-form {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }
        .login-field {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        .login-field label {
          font-size: 14px;
          font-weight: 600;
          color: var(--color-title);
        }
        .login-field input {
          padding: 10px 14px;
          border: 1px solid var(--color-border);
          border-radius: 8px;
          font-size: 15px;
          font-family: var(--font-sans);
          color: var(--color-ink);
          background: var(--color-surface);
          outline: none;
          transition: border-color 0.15s, box-shadow 0.15s;
        }
        .login-field input:focus {
          border-color: var(--color-border-focus);
          box-shadow: 0 0 0 3px var(--color-accent-ghost);
        }
        .login-field input:disabled {
          background: var(--color-bg);
          color: var(--color-muted);
        }
        .login-remember {
          display: flex;
          align-items: center;
        }
        .login-remember label {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 13px;
          color: var(--color-muted);
          cursor: pointer;
        }
        .login-remember input[type="checkbox"] {
          width: 16px;
          height: 16px;
          accent-color: var(--color-accent);
        }
        .login-submit {
          padding: 12px 24px;
          background-color: var(--color-accent);
          color: #fff;
          border: none;
          border-radius: 10px;
          font-size: 15px;
          font-weight: 600;
          font-family: var(--font-sans);
          cursor: pointer;
          transition: filter 0.15s;
        }
        .login-submit:hover:not(:disabled) {
          filter: brightness(0.93);
        }
        .login-submit:disabled {
          opacity: 0.55;
          cursor: not-allowed;
        }
        .login-error {
          background: var(--color-danger-ghost);
          border: 1px solid var(--color-danger-border);
          border-radius: 8px;
          padding: 10px 14px;
          font-size: 13px;
          color: var(--color-danger);
        }
        .login-success {
          background: var(--color-accent-ghost);
          border: 1px solid var(--color-border-strong);
          border-radius: 8px;
          padding: 10px 14px;
          font-size: 13px;
          color: var(--color-title);
        }
        .login-link {
          background: none;
          border: none;
          color: var(--color-accent);
          font-size: 13px;
          font-family: var(--font-sans);
          cursor: pointer;
          padding: 0;
          text-align: center;
        }
        .login-link:hover {
          text-decoration: underline;
        }
        .login-link:disabled {
          color: var(--color-muted);
          cursor: not-allowed;
        }
        .login-forgot {
          align-self: flex-end;
          margin-top: 2px;
        }
      `}</style>
    </div>
  );
};
